import { Button, Avatar, Menu, Tooltip } from '@kintsugi/ui';
import { useAdminProfileQuery } from '@/apis/hooks/admin-profile';
import { ADMIN_ORG_ID } from '@/apis/users-apis';
import { APP_ROUTES, ModuleInfo } from '@/constants/app-constants';
import useOrg from '@/hooks/useOrg';
import { OrganizationAndPartnerSearch } from '@/sidebar/org-and-partner-search';
import { Flex, HStack, Icon, Image, Separator, Text } from '@chakra-ui/react';
import { useAuthInfo, useLogoutFunction } from '@propelauth/react';
import { useEffect, useState } from 'react';
import { FiLogOut, FiUser } from 'react-icons/fi';
import { Link, matchRoutes, useLocation } from 'react-router-dom';
import { DoubleArrowLeftIcon, DoubleArrowRightIcon, KintsugiLogoLight, WarningIcon } from '../components/icons';
import { LinkButton, LinkButtonProps } from '../components/ui/link-button';
import {
  isAdminProfileEnabled,
  isCsmWorkspaceEnabled,
  isKintsugiTasksBoardEnabled,
  isOrgHealthCheckEnabled,
} from '@/utils/feature-flags';
import { resolveOrgRoute, resolvePartnerRoute } from '@/utils/routes';
import logoSm from './components/logo.sm.svg';
import usePartner from '@/hooks/usePartner';
import { isPartnerBilledType } from '@/pages/partners/partner-crm-labels';
import { AdminProfileModal } from './admin-profile-modal';

type NavItemProps = {
  href: string;
  title: string;
  icon: React.ReactNode;
  isExpanded: boolean;
  warningMessage?: string;
} & LinkButtonProps;

const NavItem = ({
  title,
  href,
  icon: providedIcon,
  isExpanded = true,
  children,
  warningMessage,
  ...rest
}: NavItemProps) => {
  return (
    <Tooltip
      content={title}
      showArrow
      disabled={isExpanded}
      positioning={{ placement: 'right-start', offset: { crossAxis: 4 } }}
    >
      <LinkButton size="sm" variant={'nav' as any} asChild gap="3" {...rest}>
        <Link to={href}>
          {providedIcon && <Icon boxSize="24px">{providedIcon}</Icon>}
          <HStack gap={1.5}>
            {isExpanded ? children : null}
            {isExpanded && warningMessage && (
              <Tooltip content={warningMessage} positioning={{ placement: 'bottom-end' }}>
                <WarningIcon color="orange.500" width="5" height="5" />
              </Tooltip>
            )}
          </HStack>
        </Link>
      </LinkButton>
    </Tooltip>
  );
};

// Only routes shown in the sidebar can be active. A hidden detail route that matches first
// must not stop its parent nav item from showing as active.
const findActiveNavItem = (location: ReturnType<typeof useLocation>) =>
  APP_ROUTES.find(
    ({ routesToMatch, sideBarSectionName }: ModuleInfo) =>
      !!sideBarSectionName && !!matchRoutes(routesToMatch, location)
  );

type SidebarProps = {
  hideToggleBtn?: boolean;
  isExpanded?: boolean;
  onSidebarExpand?: () => void;
  onClose?: () => void;
};
export const Sidebar = ({ hideToggleBtn = true, isExpanded = true, onSidebarExpand, onClose }: SidebarProps) => {
  const { user, accessHelper } = useAuthInfo();
  // PropelAuth's user object keeps the name and picture from sign-in, so prefer the profile saved in this session.
  const { data: savedProfile } = useAdminProfileQuery(false);
  const userAvatarSrc = savedProfile?.picture_url || user?.pictureUrl || '';
  const savedName = [savedProfile?.first_name, savedProfile?.last_name].filter(Boolean).join(' ');
  const signInName = [user?.firstName, user?.lastName].filter(Boolean).join(' ');
  const userDisplayName = savedName || signInName;
  const userAvatarName = userDisplayName || undefined;
  const location = useLocation();
  const propelLogout = useLogoutFunction();
  const org = useOrg();
  const partner = usePartner();
  const isOrgSelected = !!org?.id;
  const isPartnerSelected = !!partner?.id;

  const [activeButton, setActiveButton] = useState<string | undefined>(findActiveNavItem(location)?.name);

  useEffect(() => {
    setActiveButton(findActiveNavItem(location)?.name);
  }, [location]);

  const tasksBoardEnabled = isKintsugiTasksBoardEnabled();
  const cxBoardEnabled = isOrgHealthCheckEnabled();
  const csmWorkspaceEnabled = isCsmWorkspaceEnabled();
  const profileEnabled = isAdminProfileEnabled();
  const [profileOpen, setProfileOpen] = useState(false);

  const groupedRoutes = APP_ROUTES.reduce(
    (acc, route) => {
      // Routes without a sideBarSectionName are detail-only routes (e.g. a
      // reseller detail page) kept in APP_ROUTES for header labels. They must
      // not render as sidebar nav items.
      if (!route.sideBarSectionName) {
        return acc;
      }
      // Permission-gated nav items stay hidden unless accessHelper confirms.
      if (route.permission && !accessHelper?.hasPermission(ADMIN_ORG_ID, route.permission)) {
        return acc;
      }
      // KA-1934: board flag gates the nav item (independent of health-check UI).
      if (route.href === '/kintsugi-tasks' && !tasksBoardEnabled) {
        return acc;
      }
      if (route.href === '/kintsugi-cx' && !cxBoardEnabled) {
        return acc;
      }
      if (route.href === '/csm' && !csmWorkspaceEnabled) {
        return acc;
      }
      // KA-1118: the board replaces the legacy table when enabled.
      if (route.href === '/billing-change-requests' && tasksBoardEnabled) {
        return acc;
      }
      const sectionName = route.sideBarSectionName;
      if (!acc[sectionName]) {
        acc[sectionName] = [];
      }
      acc[sectionName].push(route);
      return acc;
    },
    {} as Record<string, ModuleInfo[]>
  );

  const isOrgOrPartnerSelected = isOrgSelected || isPartnerSelected;

  const logoutFunction = () => {
    localStorage.clear();
    propelLogout(false);
  };

  const handleNavClick = (item: ModuleInfo) => {
    setActiveButton(item.name);
    onClose?.();
  };

  const renderLogo = isExpanded ? (
    <Flex gap={1} justify={'center'} align={'center'}>
      <KintsugiLogoLight height="30px" width="124px" />
    </Flex>
  ) : (
    <Image src={logoSm} height={'20px'} width="20px" onClick={onSidebarExpand} cursor={'pointer'} />
  );

  const renderOrganizationsAndPartnersSection = (moduleInfo: ModuleInfo) => {
    const isPartnerRoute = moduleInfo.href.includes('/partners');
    const isOrganizationsAndPartnersSection = moduleInfo.sideBarSectionName === 'Organizations & Portfolios';
    let targetHref = moduleInfo.href;

    if (isOrganizationsAndPartnersSection && isPartnerSelected && !isPartnerRoute) {
      return null;
    }

    if (isOrganizationsAndPartnersSection && isOrgSelected && isPartnerRoute) {
      return null;
    }

    if (
      isOrganizationsAndPartnersSection &&
      isPartnerRoute &&
      moduleInfo.href.includes('/partners/:id/billing-access') &&
      !isPartnerBilledType(partner?.billingType)
    ) {
      return null;
    }

    if (isOrganizationsAndPartnersSection && partner && isPartnerRoute) {
      targetHref = resolvePartnerRoute(moduleInfo.href, partner.id);
    } else if (isOrganizationsAndPartnersSection && isOrgSelected) {
      targetHref = resolveOrgRoute(moduleInfo.href, org.id);
    }

    return (
      <NavItem
        key={moduleInfo.href}
        title={moduleInfo.name}
        data-onboarding={moduleInfo.name}
        data-expanded={isExpanded}
        data-active={activeButton === moduleInfo.name || undefined}
        icon={moduleInfo.icon}
        onClick={() => handleNavClick(moduleInfo)}
        href={targetHref}
        isExpanded={isExpanded}
      >
        <Text color={'white'} lineClamp={1}>
          {moduleInfo.name}
        </Text>
      </NavItem>
    );
  };

  return (
    <Flex
      as={'nav'}
      data-onboarding="sidebar"
      h={'100%'}
      bgColor="colorPalette.fg"
      flexDir="column"
      justifyContent="space-between"
      py={'16px'}
      overflowY="auto"
      overflowX={'hidden'}
    >
      <Flex px="5%" flexDirection="column">
        <Flex justify={isExpanded ? 'space-between' : 'center'} align={'center'} mb={2}>
          {renderLogo}
          {!hideToggleBtn && (
            <Button variant="ghost" onClick={onSidebarExpand} px="0" minWidth="24px">
              <Icon color="neonGreen.500">{isExpanded ? <DoubleArrowLeftIcon /> : <DoubleArrowRightIcon />}</Icon>
            </Button>
          )}
        </Flex>
        <Flex flexDirection={'column'} justifyContent={'space-between'} mt={5}>
          {Object.entries(groupedRoutes).map(([sectionName, routes], index) => (
            <Flex key={sectionName} flexDirection={'column'} justifyContent={'space-between'}>
              {isExpanded && (
                <Text fontWeight={'500'} color={'white'} fontSize={'14px'} mb={3}>
                  {sectionName}
                </Text>
              )}
              {sectionName === 'Organizations & Portfolios' && <OrganizationAndPartnerSearch placeholder={'Search'} />}
              {/* Hide Organizations section routes if no organization is selected */}
              {sectionName !== 'Organizations & Portfolios' || isOrgOrPartnerSelected ? (
                routes.map(renderOrganizationsAndPartnersSection)
              ) : (
                <Flex justifyContent={'center'} minH={'10rem'} mt={4}>
                  <Text fontSize="sm" color={'white'} lineClamp={1}>
                    Please Select Organization or Portfolio
                  </Text>
                </Flex>
              )}
              {index !== Object.entries(groupedRoutes).length - 1 && (
                <Separator colorPalette={'gray'} size="sm" my={5} />
              )}
            </Flex>
          ))}
        </Flex>
      </Flex>
      <Flex px="5%" flexDirection={'column'}>
        <Menu.Root positioning={{ placement: 'top' }}>
          {/* Flex trigger (not Button asChild): KDS Button wraps children in a
              fragment for icons/loading, which breaks Chakra asChild nesting. */}
          <Menu.Trigger asChild>
            <Flex
              gap={3}
              alignItems="center"
              justifyContent="start"
              alignSelf={isExpanded ? 'start' : 'center'}
              my={3}
              px={2.5}
              py={0}
              width={!isExpanded ? '45px' : undefined}
              minW="45px"
              cursor="pointer"
            >
              <Avatar size="sm" src={userAvatarSrc} name={userAvatarName} />
              {isExpanded && (
                <Text lineClamp={1} color={'white'}>
                  {userDisplayName}
                </Text>
              )}
            </Flex>
          </Menu.Trigger>
          <Menu.Content>
            {profileEnabled && (
              <Menu.Item value="profile" onClick={() => setProfileOpen(true)} startIcon={<FiUser />}>
                Profile
              </Menu.Item>
            )}
            <Menu.Item value="logout" onClick={logoutFunction} startIcon={<FiLogOut />}>
              Logout
            </Menu.Item>
          </Menu.Content>
        </Menu.Root>
        {profileEnabled && <AdminProfileModal open={profileOpen} onOpenChange={setProfileOpen} />}
      </Flex>
    </Flex>
  );
};
